import { useLingui } from '@lingui/react'
import { useEffect, useMemo } from 'react'
import { useSnackbar } from 'notistack'
import { t, Trans } from '@lingui/macro'
import { Control, SubmitHandler, useController, useForm } from 'react-hook-form'
import z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { LoadingButton } from '@mui/lab'
import { Box, CircularProgress, FormHelperText, IconButton, Stack, Tooltip } from '@mui/material'
import CachedRoundedIcon from '@mui/icons-material/CachedRounded'

import TextInputController from '@/components/ui/inputs/TextInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import { useGetPcscReadersQuery, useLazyReadCPxCardQuery, useSelectCPxCardMutation } from '@/core/services/dmp-connect-js2'
import { CardReaderSlotType } from '@/core/types/dmp-connect'
import { getApiErrorMessage } from '@/utils/errors'

import { useGetCPxCardsInfos } from '../model/useGetCPxCardsInfos'

const formSchema = z.object({
  cardReaderName: z.string().min(1, { error: t`Card reader is required` }),
  pinCode: z
    .string()
    .min(1, { error: t`Pin code is required` })
    .length(4, { error: t`Pin code must be 4 digits` }),
})

type SelectCpxCardFormData = z.infer<typeof formSchema>

type SelectCPxCardProps = {
  initialCardReaderName: string | null
}

export function SelectCPxCard({ initialCardReaderName }: SelectCPxCardProps) {
  const { enqueueSnackbar } = useSnackbar()

  useLingui()

  const [selectCPxCard, { isLoading: isSelectingCard }] = useSelectCPxCardMutation()
  const [readCPxCard, { isLoading: isReadingCard }] = useLazyReadCPxCardQuery()

  const rhForm = useForm<SelectCpxCardFormData>({
    defaultValues: { cardReaderName: initialCardReaderName ?? '', pinCode: '' },
    resolver: zodResolver(formSchema),
  })

  const onSubmit: SubmitHandler<SelectCpxCardFormData> = async (data) => {
    try {
      const { s_status } = await selectCPxCard({ s_readerName: data.cardReaderName, saveCard: true }).unwrap()
      if (s_status === 'ERROR') throw new Error('Cannot get cpx card')
      await readCPxCard({ s_pinCode: data.pinCode }).unwrap()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t`An error has occured while reading the card. Please try again!`), { variant: 'error' })
    }
  }

  const isSubmitting = isSelectingCard || isReadingCard

  return (
    <form onSubmit={rhForm.handleSubmit(onSubmit, (error) => console.log(error))}>
      <Stack alignItems="center" mx="auto" spacing={3}>
        <TextInputController control={rhForm.control} fullWidth inputProps={{ maxLength: 4 }} label={t`CPS code pin`} name="pinCode" type="password" />
        <SelectCardReader control={rhForm.control} />
        <LoadingButton loading={isSubmitting} variant="contained" type="submit" sx={{ width: 'fit-content' }}>
          <Trans>Continue</Trans>
        </LoadingButton>
      </Stack>
    </form>
  )
}

function SelectCardReader({ control }: { control: Control<SelectCpxCardFormData> }) {
  useLingui()

  const {
    fieldState: { error },
  } = useController({ control, name: 'cardReaderName' })

  const {
    data: getPcscReadersResult,
    isFetching: isFetchingPcscReaders,
    refetch: refetchPcscReaders,
  } = useGetPcscReadersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })

  const { cpxCardInfosMap, isFetchingCpxCardsInfos, getCPxCardsInfos } = useGetCPxCardsInfos()

  const cardReaders = useMemo(
    () => getPcscReadersResult?.Readers?.filter((reader) => reader.i_slotType === CardReaderSlotType.CPX_CARD) ?? [],
    [getPcscReadersResult]
  )

  const options = useMemo(
    () =>
      cardReaders.map((reader) => {
        const cpxCardInfos = cpxCardInfosMap[reader.s_name]
        return {
          label: `${reader.s_name} (${reader.s_slotType})` + (cpxCardInfos ? ` (${cpxCardInfos.s_birthName} ${cpxCardInfos.Givens.join(' ')})` : ''),
          value: reader.s_name,
        }
      }),
    [cardReaders, cpxCardInfosMap]
  )

  useEffect(() => {
    if (!cardReaders.length) return
    getCPxCardsInfos(cardReaders)
  }, [cardReaders])

  return (
    <Stack alignItems="center" spacing={2} width="100%">
      <Box width="100%" position="relative">
        <Tooltip placement="top" title={t`Refresh`}>
          <IconButton onClick={refetchPcscReaders} size="small" sx={{ position: 'absolute', top: '-2px', right: 0, zIndex: 1 }}>
            <CachedRoundedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <SelectInputController
          control={control}
          fullWidth
          InputLabelProps={{ error: !!error }}
          InputProps={{ error: !!error }}
          label={t`CPS card reader`}
          name="cardReaderName"
          options={!isFetchingCpxCardsInfos && !isFetchingPcscReaders ? options : []}
        />
        {!!error && (
          <FormHelperText error className="px-3.5">
            {error.message}
          </FormHelperText>
        )}
        {(isFetchingCpxCardsInfos || isFetchingPcscReaders) && (
          <Box position="absolute" top="75%" left="50%" sx={{ transform: 'translate(-50%, -50%)' }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>
    </Stack>
  )
}
